import { useState, useMemo, useCallback } from 'react';

export type MentionUser = {
  id: string;
  name: string;
  avatarUrl?: string;
};

type UseMentionInputProps = {
  users: MentionUser[];
  text: string;
  onTextChange: (text: string) => void;
};

export const useMentionInput = ({
  users,
  text,
  onTextChange,
}: UseMentionInputProps) => {
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState<number | null>(
    null,
  );

  // Filter users based on mention query
  const filteredUsers = useMemo(() => {
    if (!mentionQuery) return users;
    const query = mentionQuery.toLowerCase();
    return users.filter((user) => user.name.toLowerCase().includes(query));
  }, [users, mentionQuery]);

  // Handle text change with mention detection
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      const cursorPos = e.target.selectionStart ?? value.length;

      onTextChange(value);

      // Find if we're in a mention context (after @ symbol)
      const textBeforeCursor = value.slice(0, cursorPos);
      const lastAtIndex = textBeforeCursor.lastIndexOf('@');

      if (lastAtIndex !== -1) {
        const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
        // Check if @ is at start or after space/newline (valid mention start)
        const charBeforeAt = lastAtIndex > 0 ? value[lastAtIndex - 1] : ' ';

        if (
          charBeforeAt === ' ' ||
          charBeforeAt === '\n' ||
          lastAtIndex === 0
        ) {
          // Keep dropdown open for multi-word names
          setShowMentions(true);
          setMentionQuery(textAfterAt);
          setMentionStartIndex(lastAtIndex);
          return;
        }
      }

      setShowMentions(false);
      setMentionQuery('');
      setMentionStartIndex(null);
    },
    [onTextChange],
  );

  // Handle user selection from mention dropdown
  const handleSelectMention = useCallback(
    (user: MentionUser) => {
      if (mentionStartIndex === null) return;

      const beforeMention = text.slice(0, mentionStartIndex);
      const afterMention = text.slice(
        mentionStartIndex + 1 + mentionQuery.length,
      );
      const newText = `${beforeMention}@${user.name} ${afterMention}`;

      onTextChange(newText);
      setShowMentions(false);
      setMentionQuery('');
      setMentionStartIndex(null);
    },
    [text, mentionStartIndex, mentionQuery, onTextChange],
  );

  // Close mentions dropdown
  const closeMentions = useCallback(() => {
    setShowMentions(false);
    setMentionQuery('');
    setMentionStartIndex(null);
  }, []);

  return {
    showMentions,
    filteredUsers,
    handleChange,
    handleSelectMention,
    closeMentions,
  };
};
